import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Circle, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const ConductAudit = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [auditSheets, setAuditSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [callId]);

  const fetchData = async () => {
    try {
      const [callResponse, sheetsResponse] = await Promise.all([
        axios.get(`${API}/calls/${callId}`),
        axios.get(`${API}/audit-sheets`)
      ]);
      setCall(callResponse.data);
      setAuditSheets(sheetsResponse.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const handleSheetSelect = async (sheetId) => {
    const sheet = auditSheets.find(s => s.id === sheetId);
    setSelectedSheet(sheet);
    setResponses({});
  };

  const handleResponse = (questionId, value) => {
    setResponses({ ...responses, [questionId]: value });
  };

  const handleSubmit = async () => {
    if (!selectedSheet) {
      toast.error('Please select an audit sheet');
      return;
    }

    // Check required questions
    const requiredQuestions = selectedSheet.questions.filter(q => q.required);
    const missingResponses = requiredQuestions.filter(q => !responses[q.id]);
    
    if (missingResponses.length > 0) {
      toast.error(`Please answer all required questions (${missingResponses.length} remaining)`);
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('responses', JSON.stringify(responses));
      if (notes) formData.append('notes', notes);

      await axios.post(
        `${API}/audit-sheets/${selectedSheet.id}/assess/${callId}`,
        formData
      );

      toast.success('Audit submitted successfully!');
      navigate(`/assessment/${callId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit audit');
    } finally {
      setSubmitting(false);
    }
  };

  const getProgress = () => {
    if (!selectedSheet) return 0;
    const answered = Object.keys(responses).length;
    const total = selectedSheet.questions.length;
    return Math.round((answered / total) * 100);
  };

  const groupQuestionsByCategory = () => {
    if (!selectedSheet) return {};
    return selectedSheet.questions.reduce((acc, q) => {
      const category = q.category || 'General';
      if (!acc[category]) acc[category] = [];
      acc[category].push(q);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-600">Loading audit form...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="conduct-audit-page">
        {/* Header */}
        <div>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-button">
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Conduct Audit
          </h1>
          <p className="text-slate-600">Call: {call?.title}</p>
        </div>

        {/* Sheet Selection */}
        {!selectedSheet ? (
          <Card className="p-6 border border-slate-200 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Select Audit Sheet
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {auditSheets.map((sheet) => (
                <Card
                  key={sheet.id}
                  className="p-4 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer transition-all"
                  onClick={() => handleSheetSelect(sheet.id)}
                  data-testid={`select-sheet-${sheet.id}`}
                >
                  <h3 className="font-bold text-slate-900 mb-1">{sheet.name}</h3>
                  <p className="text-sm text-slate-600 mb-2">{sheet.description}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{sheet.questions.length} questions</span>
                    <span>•</span>
                    <span>Max score: {sheet.total_score}</span>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        ) : (
          <>
            {/* Progress Bar */}
            <Card className="p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-slate-900">{selectedSheet.name}</p>
                  <p className="text-sm text-slate-600">
                    {Object.keys(responses).length} of {selectedSheet.questions.length} answered
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-600">{getProgress()}%</p>
                  <p className="text-xs text-slate-600">Complete</p>
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all"
                  style={{ width: `${getProgress()}%` }}
                />
              </div>
            </Card>

            {/* Questions by Category */}
            <div className="space-y-6">
              {Object.entries(groupQuestionsByCategory()).map(([category, questions]) => (
                <Card key={category} className="p-6 border border-slate-200 shadow-sm">
                  <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {category}
                  </h3>
                  <div className="space-y-6">
                    {questions.map((question, index) => (
                      <div
                        key={question.id}
                        className="pb-6 border-b border-slate-200 last:border-0 last:pb-0"
                        data-testid={`question-${question.id}`}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          {responses[question.id] ? (
                            <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0 mt-1" />
                          ) : (
                            <Circle size={20} className="text-slate-300 flex-shrink-0 mt-1" />
                          )}
                          <div className="flex-1">
                            <Label className="text-base font-medium text-slate-900">
                              {question.question}
                              {question.required && <span className="text-rose-600 ml-1">*</span>}
                            </Label>
                            <p className="text-xs text-slate-500 mt-1">
                              Weight: {question.weight} points
                            </p>
                          </div>
                        </div>

                        {/* Answer Input */}
                        <div className="ml-8">
                          {question.type === 'boolean' && (
                            <div className="flex gap-3">
                              <Button
                                type="button"
                                variant={responses[question.id] === 'yes' ? 'default' : 'outline'}
                                className={responses[question.id] === 'yes' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                onClick={() => handleResponse(question.id, 'yes')}
                                data-testid={`yes-${question.id}`}
                              >
                                Yes
                              </Button>
                              <Button
                                type="button"
                                variant={responses[question.id] === 'no' ? 'default' : 'outline'}
                                className={responses[question.id] === 'no' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                                onClick={() => handleResponse(question.id, 'no')}
                                data-testid={`no-${question.id}`}
                              >
                                No
                              </Button>
                            </div>
                          )}

                          {question.type === 'rating' && (
                            <div className="flex gap-2">
                              {[1, 2, 3, 4, 5].map((rating) => (
                                <Button
                                  key={rating}
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={`w-12 h-12 ${responses[question.id] === rating ? 'bg-amber-100 border-amber-400' : ''}`}
                                  onClick={() => handleResponse(question.id, rating)}
                                  data-testid={`rating-${question.id}-${rating}`}
                                >
                                  <div className="flex flex-col items-center">
                                    <Star
                                      size={16}
                                      className={responses[question.id] >= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}
                                    />
                                    <span className="text-xs mt-1">{rating}</span>
                                  </div>
                                </Button>
                              ))}
                            </div>
                          )}

                          {question.type === 'text' && (
                            <Textarea
                              placeholder="Enter your response..."
                              value={responses[question.id] || ''}
                              onChange={(e) => handleResponse(question.id, e.target.value)}
                              data-testid={`text-${question.id}`}
                              rows={3}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>

            {/* Additional Notes */}
            <Card className="p-6 border border-slate-200 shadow-sm">
              <Label htmlFor="audit-notes" className="text-base font-medium text-slate-900 mb-2 block">
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="audit-notes"
                placeholder="Add any additional observations or comments..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="audit-notes"
                rows={4}
              />
            </Card>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setSelectedSheet(null)}
                data-testid="change-sheet-button"
              >
                Change Audit Sheet
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-[#4338CA] hover:bg-[#3730A3] text-white"
                disabled={submitting || getProgress() < 100}
                data-testid="submit-audit-button"
              >
                {submitting ? 'Submitting...' : 'Submit Audit'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ConductAudit;